import React, { useEffect, useState } from 'react';
import { Button, FormControl, FormHelperText, FormGroup, CircularProgress } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import testDateService from '../../../../services/TestDateService';
import { ModalCustomerSelector } from '../../../Common/CustomerSelector';
import ProductSelectList from '../../../Common/Selectors/ProductSelectList';
import { CustomerDTO, ProductDTO, ProductId, SiteDTO } from '@beable/roster-backend-client-v1';
import SiteSelector from '../../../Common/SiteSelector';
import AcademicYearSelector from '../../../Common/AcademicYearSelector';
import TestAheadCourseCodeSelector from '../../../Common/Selectors/TestAheadCourseCodeSelector';
import { DatePicker } from '../../../Common/DatePicker/DatePicker';
import {
    defaultTestDateValidation, 
    validateForm, 
    getWarningMessage, 
    getUtcDateString, 
    validateTestDate, 
    recommendedDate, 
    minRecommendedDateDate
} from '../TestDate.helper';
import FormModal, { getResultMessage, ModalMessageSettings } from '../../../Common/FormModal';
import { getStyles } from '../styles';

const ModalSiteSelector = styled(SiteSelector)(({ theme }) => getStyles(theme).modalSelector);
const ModalProductSelectList = styled(ProductSelectList)(({ theme }) => getStyles(theme).modalSelector);
const ModalAcademicYearSelector = styled(AcademicYearSelector)(({ theme }) => getStyles(theme).modalSelector);
const ModalTestAheadCourseCodeSelector = styled(TestAheadCourseCodeSelector)(({ theme }) => getStyles(theme).modalSelector);

export default function AddNewDateModal(props: any) {
    const theme = useTheme();
    const styles = getStyles(theme);
    const [ validation, setValidation ] = useState(defaultTestDateValidation);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ modalMessage, setModalMessage ] = useState<ModalMessageSettings>();
    const [ shouldEnableSaveButton, setShouldEnableSaveButton ] = useState<boolean>(false);
    const [customerId, setCustomerId] = React.useState<string>('');
    const [siteId, setSiteId] = React.useState<number>(0);
    const [productId, setProductId] = React.useState<string>(ProductId.Exampal); // this sets Exampal as default product. This should be removed and the product selector should be enabled once other products require test dates.
    const [academicYearId, setAcademicYearId] = React.useState<string>('');
    const [courseCode, setCourseCode] = React.useState<string>('');
    const [testDate, setTestDate] = React.useState<string>('');
    const [dateWarning, setDateWarning] = React.useState<string>();

    useEffect(
        () => {
            const validation = defaultTestDateValidation;

            if(!testDate) {
                setTestDate(recommendedDate);
            } else {
                validation.testDate = validateTestDate(getUtcDateString(testDate)).dateError;
            }
            setValidation(validation);

            if (!siteId || !customerId || !productId || !academicYearId || !courseCode || !testDate || validation.testDate.length) {
                setShouldEnableSaveButton(false);
            } else {
                setShouldEnableSaveButton(true);
            }
        },
        [ siteId, customerId, productId, academicYearId, courseCode, testDate ]
    );

    const resetState = () => {
        setValidation(defaultTestDateValidation);
        setIsLoading(false);
    }

    const submitForm = async () => {  
      const validation = await validateForm(customerId, siteId, productId, academicYearId, courseCode, testDate);

      if (validation.isInvalid) {
        setValidation(validation.validation);
      } else {
        setValidation(defaultTestDateValidation);
        setIsLoading(true);
        const { validation, error, success } = await testDateService.saveTestDate(customerId, siteId, productId, academicYearId, courseCode, testDate);
        setIsLoading(false);
        if(validation && validation.isInvalid) {
            setValidation(validation.validation);
        } else {
            const message = getResultMessage(success ? true : undefined, success, error);
            setModalMessage(message);
        }
      }
    };

    const handleDateChange = (date: Date) => {
        const dateString = getUtcDateString(date);
        setTestDate(dateString);
        setDateWarning(getWarningMessage(dateString));
    }

    return (
        <FormModal
            title="Create new test date"
            description="Use the form below to add a test date."
            isOpen={props.open}
            handleClose={() => {
                props.handleClose();
                setModalMessage(undefined);
            }}
            message={modalMessage}
        >
            <div>
                <FormGroup sx={{ alignItems: 'stretch' }}>
                    <FormControl variant="standard" fullWidth sx={[styles.modalSelector, styles.mb2]}>
                        <DatePicker
                            label='Test Date'
                            value={testDate ? new Date(testDate) : null}
                            onChange={handleDateChange}
                            minDate={minRecommendedDateDate}
                        />
                        {dateWarning ? <FormHelperText sx={styles.redTxt}>{dateWarning}</FormHelperText> : undefined}
                    </FormControl>
                    <FormControl variant="standard" fullWidth>
                        <ModalCustomerSelector onSelect={(customer: CustomerDTO) => setCustomerId(customer?.id)} />
                        {validation.customerId ? <FormHelperText sx={styles.redTxt}>{validation.customerId}</FormHelperText> : undefined}
                    </FormControl>
                    <FormControl variant="standard" fullWidth>
                        <ModalSiteSelector
                            // siteId={siteId} // this line could cause "Error: Maximum update depth exceeded on setSiteId(0)"
                            customerId={customerId}
                            onSelect={(site: SiteDTO) => setSiteId(site?.id)}
                            disabled={!customerId}
                        />
                        {validation.siteId ? <FormHelperText sx={styles.redTxt}>{validation.siteId}</FormHelperText> : undefined}
                    </FormControl>
                    <FormControl variant="standard" fullWidth>
                        <ModalProductSelectList
                            productId={productId}
                            customerId={customerId}
                            siteId={siteId}
                            onChange={(product: ProductDTO) => {
                                // This block must be changed when we have other products than Exampal for Test dates
                                if(!product || (product.id && (product.id as ProductId) !== ProductId.Exampal)) {
                                    setProductId('');
                                } else {
                                    setProductId(ProductId.Exampal);
                                }
                                // product?.id && setProductId(product.id)
                            } }
                            // disabled={!testDate?.customerId}
                            disabled
                        />
                        {validation.siteId ? <FormHelperText sx={styles.redTxt}>{validation.siteId}</FormHelperText> : undefined}
                    </FormControl>
                    <FormControl variant="standard" fullWidth>
                        <ModalAcademicYearSelector
                            siteId={siteId}
                            handleSelection={(academicYear: any) => { setAcademicYearId(academicYear?.academicYearId)} }
                            clearAcademicYear={!academicYearId}
                            disabled={!siteId}
                        />
                        {validation.academicYearId ? <FormHelperText sx={styles.redTxt}>{validation.academicYearId}</FormHelperText> : undefined}
                    </FormControl>
                    <FormControl variant="standard" fullWidth>
                        <ModalTestAheadCourseCodeSelector
                            courseCode={courseCode}
                            onChange={(courseCode: any) => { setCourseCode(courseCode)} }
                        />
                        {validation.courseCode ? <FormHelperText sx={styles.redTxt}>{validation.courseCode}</FormHelperText> : undefined}
                    </FormControl>
                </FormGroup>
                <FormGroup>
                    {isLoading ? <CircularProgress color="primary" /> : undefined}
                </FormGroup>
                <FormGroup sx={[styles.mt2, styles.dfr]}>
                    <Button variant="contained" color="primary" onClick={submitForm} disabled={!shouldEnableSaveButton}>Save test date</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            resetState();
                            props.handleClose();
                        }}
                        sx={styles.ml2}>Close</Button>
                </FormGroup>
            </div>
        </FormModal >
    );
}